/**
 * En-tête global du site (logo, navigation, compte)
 *
 * Rôle : Barre fixe avec logo Otulaa, liens Gestion / E-commerce / Communauté (selon plan), accès compte et déconnexion.
 * Charge le plan utilisateur via /api/users/me et adapte les liens selon le statut (superadmin, connecté, plan Découverte).
 * Technologies : React (client), next-auth (useSession), Next.js (Link, useRouter, usePathname)
 */
"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { isSuperAdminEmail } from "@/lib/auth/constants";

export default function Header() {
  const { status, data: session } = useSession();
  const isLoggedIn = status === "authenticated";
  const [isAccountAnimating, setIsAccountAnimating] = useState(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [gestionRedirectUrl, setGestionRedirectUrl] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  useEffect(() => { /* noop trigger on route change if needed */ }, [pathname]);

  // Charger le plan de l'utilisateur et vérifier si superadmin
  useEffect(() => {
    if (isLoggedIn && session?.user?.id) {
      fetch('/api/users/me')
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            const plan = data.subscription?.plan || data.user?.plan || 'Découverte';
            setUserPlan(plan);
            const superAdmin =
              Boolean(data.user?.is_superadmin) || isSuperAdminEmail(data.user?.email);
            setIsSuperAdmin(superAdmin);
            setGestionRedirectUrl(data.company?.gestion_redirect_url ?? null);
          }
        })
        .catch(err => console.error('Error loading user plan:', err));
    }
  }, [isLoggedIn, session]);

  // Masque complètement le header sur les pages d'auth
  if (pathname === "/signin" || pathname === "/signup") {
    return null;
  }

  const handleAccountClick = () => {
    setIsAccountAnimating(true);
    
    setTimeout(() => {
      router.push("/account");
      setIsAccountAnimating(false);
    }, 300);
  };

  // Afficher les 3 liens (Gestion, E-commerce, Communauté) dès qu'on est connecté, y compris sur ces pages
  const isDiscoveryPlan = userPlan === "Découverte";
  const showNavLinks = status !== "loading" && isLoggedIn && !isDiscoveryPlan;

  return (
    <header className="fixed top-0 left-0 right-0 w-full bg-gray-50 border-b border-gray-200 z-50">
      <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between w-full relative">                                                                    
        {/* Gauche : logo Otulaa */}
        <div className="flex items-center gap-4">
          <Link href="/dashboard" aria-label="Retour au dashboard" className="flex items-center gap-3 group/header-logo">
            <Image
              src="/logos/otulaa-rond.png"
              alt="Otulaa Logo"
              width={40}
              height={40}
              priority
              className="cursor-pointer w-8 h-8 transition-transform duration-700 ease-out group-hover/header-logo:rotate-360 group-hover/header-logo:animate-spin"
            />
            <Image
              src="/logos/otulaa-texte.png"
              alt="Otulaa"
              width={80}
              height={20}
              priority
              className="cursor-pointer"
            />
          </Link>
        </div>

        {/* Centre : Gestion, E-commerce, Communauté (toujours visibles quand connecté) */}
        {showNavLinks && (
          <div className="absolute left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-3">
            <a
              href={gestionRedirectUrl || (isSuperAdmin ? "https://crm.otulaa.com" : "/gestion")}
              target={gestionRedirectUrl || isSuperAdmin ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="inline-flex w-32 justify-center items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95"
            >
              Gestion
            </a>
            <Link
              href="/dashboard/ecommerce"
              className="inline-flex w-32 justify-center items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95"
            >
              E-commerce
            </Link>
            <Link
              href="/communaute"
              className="inline-flex w-32 justify-center items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95"
            >
              Communauté
            </Link>
          </div>
        )}

        {/* Droite : boutons si connecté */}
        <nav className="flex items-center gap-3">
          {status === "loading" ? (
            <div className="text-sm text-gray-500">Chargement...</div>
          ) : isLoggedIn ? (
            <>
              <button
                onClick={handleAccountClick}
                disabled={isAccountAnimating}
                className={`inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md hover:bg-gray-50 hover:border-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/10 active:scale-95 ${
                  isAccountAnimating 
                    ? 'opacity-70 scale-95 translate-x-1' 
                    : 'opacity-100 scale-100 translate-x-0'
                }`}
                type="button"
              >
                <span className="transition-all duration-300">
                  {isAccountAnimating ? 'Ouverture...' : 'Mon Compte'}
                </span>
              </button>
            </>
          ) : (
            <Link
              href="/signin"
              className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md hover:bg-gray-50 hover:border-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/10 active:scale-95"
            >
              Se connecter
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
